/**
 * redaction — le rendu des champs caviardés par le serveur (UPT-2039, phase 1 web).
 *
 * Le serveur remplace toute valeur au-dessus de l'habilitation de l'appelant par la marque exacte
 * `"••••"` (quatre U+2022), quel que soit son type (texte, nombre, date, booléen), et ajoute à
 * l'enregistrement `_classification: { redacted, levels, compartments }` — SEULEMENT si au moins
 * un champ a été caviardé. En écriture, renvoyer la marque (ou omettre le champ) conserve la valeur
 * en base ; toute autre valeur est refusée (403 `classification.fieldReadOnly`).
 *
 * Règle d'or inchangée : le web ne décide JAMAIS de la visibilité. Ce module lit ce que le serveur
 * dit (la marque, `_classification`) et protège le web de ses propres pièges : jamais de formatage
 * numérique ou de date sur la marque (sinon `NaN` / `Invalid Date`), jamais de marque renvoyée à
 * l'enregistrement, jamais deux marques collées dans un libellé composé (« •••• •••• »).
 *
 * Module PUR (ni React, ni DOM) : testé sous jest (`redaction.test.ts`). Les accesseurs de clés
 * i18n sont ici, au niveau module : aucune clé composée dans le JSX.
 */

import {
  REDACTED_MARK,
  isRedacted,
  normalizeCompartments,
  parseClassificationError,
  parseLevel,
  redactedFields,
  redactedLevel,
  type ClassificationTranslate,
  type ConfidentialityLevel,
} from './classification';

/** La marque posée par le serveur à la place d'une valeur caviardée (quatre U+2022). */
export const REDACTION_MARK = REDACTED_MARK;

export { redactedFields };

/** `_classification` d'un enregistrement, tel que le serveur l'émet (présent seulement si un champ est caviardé). */
export type RecordClassification = {
  /** Clés caviardées de l'enregistrement. */
  redacted: string[];
  /** Niveau (C1 à C4) de chaque champ caviardé. */
  levels: Record<string, ConfidentialityLevel>;
  /** Compartiments exigés par chaque champ caviardé. */
  compartments: Record<string, string[]>;
};

/** Pourquoi un champ est caviardé : niveau exigé (`null` si le serveur ne l'a pas dit) et compartiments. */
export type RedactionInfo = {
  level: ConfidentialityLevel | null;
  compartments: string[];
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Clé de `target` qui désigne `field` : la casse du dictionnaire d'abord, puis insensible à la casse. */
const keyOf = (target: Record<string, unknown>, field: string): string | undefined => {
  if (Object.prototype.hasOwnProperty.call(target, field)) return field;
  const wanted = field.toLowerCase();
  return Object.keys(target).find((candidate) => candidate.toLowerCase() === wanted);
};

// ─── La marque ────────────────────────────────────────────────────────────

/** Vrai si la valeur est EXACTEMENT la marque du serveur (jamais une chaîne qui la contient). */
export const isRedactedValue = (value: unknown): boolean => value === REDACTION_MARK;

const ONLY_MARKS = new RegExp(`^${REDACTION_MARK}(?:\\s+${REDACTION_MARK})*$`);

const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Vrai si le texte est la marque seule, ou plusieurs marques collées (« •••• •••• » d'un libellé composé). */
export const isRedactedLabel = (text: unknown): boolean =>
  typeof text === 'string' && ONLY_MARKS.test(text.trim());

/** Vrai si le texte contient la marque (un libellé composé d'une partie caviardée). */
export const containsRedactionMark = (text: unknown): boolean =>
  typeof text === 'string' && text.includes(REDACTION_MARK);

/**
 * Libellé composé de plusieurs valeurs (`${prénom} ${nom}`) : les parties vides, `null` et
 * `undefined` sont ignorées (jamais « undefined »), et des marques voisines ne donnent qu'UNE marque
 * (« •••• •••• » -> « •••• »), quel que soit le séparateur (« •••• · •••• » -> « •••• »). Une partie
 * en clair à côté d'une marque reste telle quelle.
 */
export const composeLabel = (parts: ReadonlyArray<unknown>, separator = ' '): string => {
  const pieces = parts
    .map((part) => (typeof part === 'string' || typeof part === 'number' ? String(part).trim() : ''))
    .filter((part) => part !== '');
  const between = separator.trim() === '' ? '\\s+' : `\\s*${escapeRegExp(separator.trim())}\\s*`;
  const run = new RegExp(`${REDACTION_MARK}(?:${between}${REDACTION_MARK})+`, 'g');
  return pieces.join(separator).replace(run, REDACTION_MARK);
};

/**
 * Libellé à ÉCRIRE quelque part (nom d'un destinataire de tâche, `patientname` d'un devis créé) : jamais
 * une marque ni un nom à demi caviardé, qui seraient enregistrés comme une donnée. Vide ou contenant la
 * marque -> `fallback` (une clé métier lisible, ou vide : le serveur complète à l'enregistrement).
 */
export const labelForWrite = (label: unknown, fallback = ''): string => {
  const text = typeof label === 'string' ? label.trim() : '';
  return text === '' || containsRedactionMark(text) ? fallback : text;
};

/**
 * Initiales d'un avatar (prénom, nom) : l'initiale de chaque partie lisible, jamais celle d'une marque
 * (« •• »). Toutes les parties caviardées : une seule puce ; aucune partie du tout : « ? ».
 */
export const initialsFrom = (parts: ReadonlyArray<unknown>): string => {
  const readable = parts
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter((part) => part !== '' && !containsRedactionMark(part));
  const initials = readable.map((part) => part.charAt(0)).join('').toUpperCase();
  if (initials !== '') return initials;
  return parts.some((part) => containsRedactionMark(part)) ? '•' : '?';
};

// ─── Lecture de `_classification` ─────────────────────────────────────────

/** Compartiments que `_classification.compartments` déclare pour un champ (casse du dictionnaire d'abord, puis insensible). */
const declaredCompartments = (record: Record<string, unknown>, field: string): string[] => {
  const meta = isObject(record._classification) ? record._classification : null;
  const byField = meta && isObject(meta.compartments) ? meta.compartments : null;
  const key = byField ? keyOf(byField, field) : undefined;
  return byField && key !== undefined && Array.isArray(byField[key]) ? normalizeCompartments(byField[key]) : [];
};

/** `_classification` normalisée d'un enregistrement ; `null` quand le serveur n'a rien caviardé. */
export const recordClassificationOf = (record: unknown): RecordClassification | null => {
  if (!isObject(record) || !isObject(record._classification)) return null;
  const redacted = redactedFields(record);
  const levels: Record<string, ConfidentialityLevel> = {};
  const compartments: Record<string, string[]> = {};
  for (const field of redacted) {
    const level = redactedLevel(record, field);
    if (level !== null) levels[field] = level;
    const codes = declaredCompartments(record, field);
    if (codes.length > 0) compartments[field] = codes;
  }
  return redacted.length === 0 ? null : { redacted, levels, compartments };
};

/**
 * Ce que le serveur dit d'un champ de `record` : `null` si le champ est lisible ; sinon le niveau
 * et les compartiments exigés. Un champ est caviardé s'il est listé par `_classification.redacted`
 * OU si sa valeur est la marque (réponse d'un serveur qui n'annonce pas `_classification`).
 */
export const redactionOf = (record: unknown, field: string): RedactionInfo | null => {
  if (!isObject(record) || typeof field !== 'string' || field === '') return null;
  const key = keyOf(record, field);
  const byValue = key !== undefined && isRedactedValue(record[key]);
  if (!byValue && !isRedacted(record, field)) return null;
  return { level: redactedLevel(record, field), compartments: declaredCompartments(record, field) };
};

/** Élément de dictionnaire : la clé du champ et, si le serveur l'a servie, sa classification effective. */
export type ClassifiedSchema = {
  key: string;
  classification?: { level?: string | null; compartments?: readonly string[] | null } | null;
};

/**
 * Caviardage d'UN champ de formulaire : `valeur` est la valeur courante du champ, `meta` le
 * `_classification` de l'enregistrement. Le champ est caviardé si le serveur le liste OU si sa
 * valeur est la marque. Le niveau et les compartiments viennent du serveur ; à défaut, de la
 * classification effective du dictionnaire (`FieldSchema.classification`) — une aide d'affichage,
 * jamais une décision : sans l'un ni l'autre, le niveau reste inconnu (`null`).
 */
export const redactionForField = (
  field: ClassifiedSchema,
  value: unknown,
  meta: RecordClassification | null | undefined,
): RedactionInfo | null => {
  const record: Record<string, unknown> = { [field.key]: value };
  if (meta) record._classification = meta;
  const declared = redactionOf(record, field.key);
  if (declared === null) return null;
  const dictionary = field.classification;
  return {
    level: declared.level ?? parseLevel(dictionary?.level),
    compartments:
      declared.compartments.length > 0 ? declared.compartments : normalizeCompartments(dictionary?.compartments),
  };
};

/** Vrai si `field` est caviardé dans `record` (voir `redactionOf`). */
export const isRedactedField = (record: unknown, field: string): boolean => redactionOf(record, field) !== null;

/** Toutes les clés caviardées d'un enregistrement : celles de `_classification` et celles dont la valeur est la marque. */
export const allRedactedFields = (record: unknown): string[] => {
  if (!isObject(record)) return [];
  const fields = new Set<string>(redactedFields(record));
  for (const [key, value] of Object.entries(record)) {
    if (isRedactedValue(value)) fields.add(key);
  }
  return [...fields];
};

/** Texte de l'info-bulle : « C3 · SANTE » (niveau puis compartiments) ; vide quand le serveur n'a rien dit. */
export const redactionDetail = (info: RedactionInfo | null | undefined): string => {
  if (!info) return '';
  return [info.level ?? '', ...info.compartments].filter((part) => part !== '').join(' · ');
};

// ─── Écriture ─────────────────────────────────────────────────────────────

/**
 * Charge utile prête à écrire, SANS les champs caviardés : le serveur restaure la valeur en base
 * pour tout champ absent (ou portant la marque). Sont retirés : les `fields` donnés, les champs de
 * `payload._classification.redacted`, toute clé dont la valeur est la marque, et `_classification`
 * elle-même (que le serveur ignore). Copie superficielle : l'entrée n'est pas modifiée.
 */
export const stripRedactedForWrite = <T extends Record<string, unknown>>(
  payload: T,
  fields: readonly string[] = [],
): Partial<T> => {
  if (!isObject(payload)) return payload;
  const dropped = new Set<string>(
    [...fields, ...allRedactedFields(payload)].map((field) => field.toLowerCase()),
  );
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (key === '_classification') continue;
    if (dropped.has(key.toLowerCase())) continue;
    out[key] = value;
  }
  return out as Partial<T>;
};

/** Valeurs d'un formulaire sans les champs caviardés — pour les surcouches d'édition (cascade, comportements). */
export const withoutRedactedValues = (values: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    if (!isRedactedValue(value)) out[key] = value;
  }
  return out;
};

// ─── Erreurs d'écriture (403 classification.fieldReadOnly) ────────────────

/** Code du serveur quand une écriture touche un champ au-dessus de l'habilitation. */
export const FIELD_READ_ONLY_CODE = 'classification.fieldReadOnly';

/** Clé du message traduit (« Le champ … est confidentiel et ne peut pas être modifié… »). */
export const FIELD_READ_ONLY_MESSAGE_KEY = 'core.classification.redaction.error.fieldReadOnly';

/** Champs nommés par un 403 `classification.fieldReadOnly` ; `null` pour toute autre erreur. */
export const fieldReadOnlyFields = (error: unknown): string[] | null => {
  const parsed = parseClassificationError(error);
  return parsed.code === FIELD_READ_ONLY_CODE && (parsed.status === 403 || parsed.status === null)
    ? parsed.fields
    : null;
};

/** Élément de dictionnaire dont on lit le libellé (clé du champ + libellé ou clé de traduction). */
export type LabelledSchema = { key: string; label?: string | { i18n: string } | null; compute?: { kind: string } };

/**
 * Libellé lisible d'un champ du dictionnaire : le libellé du schéma traduit (le texte sert d'id avec
 * lui-même pour repli, comme le moteur de formulaire), sinon la clé du champ telle quelle.
 */
export const fieldLabelResolver =
  (schemas: ReadonlyArray<LabelledSchema>, translate: (text: string) => string) =>
  (field: string): string => {
    const wanted = field.toLowerCase();
    const schema = schemas.find((candidate) => candidate.key.toLowerCase() === wanted);
    if (!schema || schema.label == null) return field;
    const raw = typeof schema.label === 'string' ? schema.label : schema.label.i18n;
    return raw ? translate(raw) : field;
  };

/**
 * Message traduit d'un refus d'écriture sur un champ caviardé, qui NOMME les champs (libellés du
 * dictionnaire) ; `null` si l'erreur n'est pas ce refus (l'appelant garde alors son message habituel).
 * Le texte brut du serveur n'est jamais repris.
 */
export const describeFieldReadOnlyError = (
  error: unknown,
  labelOf: (field: string) => string,
  translate: ClassificationTranslate,
): string | null => {
  const fields = fieldReadOnlyFields(error);
  if (fields === null) return null;
  const labels = [...new Set(fields.map((field) => labelOf(field)))];
  return translate(FIELD_READ_ONLY_MESSAGE_KEY, { count: labels.length, fields: labels.join(', ') });
};
